import { useState } from 'react';
import {
  Stack, TextInput, Textarea, Button, Group, Text,
  Checkbox, ScrollArea, Box,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { OpcionesGrandes } from './OpcionesGrandes';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';


import {
  YEARS_OPTIONS, PRACTICE_OPTIONS, INTEREST_OPTIONS, MAX_INTERESTS,
  KNEW_PARTNER_OPTIONS, MARCAS,
} from '../../utils/cvdl';
import { useKiosk } from '../../context/KioskContext/KioskContext';
import { api } from '../../wheel-api/client';
import { BRAND } from '../../theme/kioskTheme';

export function Ficha() {
  const { phoneRaw, tagRaw, setAttendee, setPhoneRaw, rutas, flujo } = useKiosk();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      name: '', phone: phoneRaw, email: '', city: '',
      yearsSinceGraduation: '', practiceContext: [],
      knewPartner: '',
      interestAreas: [], doubtCase: '',
      consentData: false, consentImage: false,
    },
    validate: {
      name: v => (v.trim().length < 3 ? 'Poné tu nombre y apellido' : null),
      // Con + adelante es un número del exterior y lo valida el backend con
      // libphonenumber. La regla de 10 dígitos es la argentina (área + número)
      // y rechazaba a cualquiera que no fuera de acá.
      phone: (v) => {
        const d = String(v).replace(/\D/g, '');
        if (String(v).startsWith('+')) {
          return d.length < 8 ? 'Revisá el número con código de país' : null;
        }
        return d.length < 10 ? 'Poné el código de área y el número' : null;
      },
      email: v => (v && !/^\S+@\S+\.\S+$/.test(v) ? 'Revisá el email' : null),
    },
  });

  function toggleArea(next) {
    if (next.length > MAX_INTERESTS) {
      notifications.show({ message: `Elegí hasta ${MAX_INTERESTS} áreas`, color: 'orange' });
      return;
    }
    form.setFieldValue('interestAreas', next);
  }

  async function enviar(v) {
    setLoading(true);
    try {
      const { attendee } = await api.createAttendee({
        name: v.name.trim(),
        phone: v.phone,
        email: v.email.trim() || undefined,
        ficha: {
          yearsSinceGraduation: v.yearsSinceGraduation || undefined,
          practiceContext: v.practiceContext.length ? v.practiceContext : undefined,
          knewPartner: v.knewPartner || undefined,
          interestAreas: v.interestAreas,
          doubtCase: v.doubtCase.trim() || undefined,
          city: v.city.trim() || undefined,
          country: 'AR',
        },
        // partner queda en false: la casilla de novedades de IPG se sacó de la
        // ficha. El campo sigue en el schema para no romper lo ya guardado.
        consent: { data: v.consentData, image: v.consentImage, partner: false },
        // Si vino del check-in por caravana, ya no se lo preguntamos de nuevo.
        ...(tagRaw ? { tagNumber: Number(tagRaw) } : {}),
        source: 'KIOSK',
      });
      // Misma forma que devuelve /checkin: la pantalla de entrega lee
      // attendee.consentImage, no attendee.consent.image.
      setAttendee({ ...attendee, consentImage: v.consentImage });
      setPhoneRaw(v.phone);
      navigate(rutas.destino);
    } catch (err) {
      // El mensaje que ve la persona es corto; el detalle va a la consola.
      console.error('[ficha]', err.code, err.status, err.data);

      const MENSAJES = {
        TOO_MANY_INTERESTS: 'Elegí hasta 2 áreas.',
        CONSENT_REQUIRED: 'Falta aceptar que te contactemos.',
        NAME_AND_PHONE_REQUIRED: 'Necesitamos tu nombre y tu WhatsApp.',
        TAG_NUMBER_TAKEN: 'Ese número de caravana ya está cargado.',
        TAG_NUMBER_REQUIRED: 'Necesitamos tu número de caravana.',
        INVALID_TAG_NUMBER: 'Revisá el número de caravana.',
        INVALID_PHONE: 'Ese número de WhatsApp no parece válido. Revisalo.',
        DUPLICATE_KEY: 'Hay un conflicto de datos. Avisale al equipo.',
        TIMEOUT: 'El servidor tardó demasiado. Probá de nuevo.',
        NETWORK: 'Sin conexión con el servidor.',
      };

      notifications.show({
        message: MENSAJES[err.code] || `No pudimos guardar tus datos (${err.code || 'error'}). Probá de nuevo.`,
        color: 'red',
        autoClose: 6000,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={form.onSubmit(enviar)} style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <Stack style={{ flex: 1, minHeight: 0 }} gap={0}>
        <ScrollArea style={{ flex: 1 }} px="8%">
          <Stack gap="lg" py="md">
            <TextInput label="Nombre y apellido" {...form.getInputProps('name')} />

            {/* El prefijo +54 9 se muestra solo mientras no escriban un +: si
                empieza con +, el número es del exterior y el prefijo fijo
                mentiría sobre lo que se va a guardar. */}
            <TextInput
              label="WhatsApp" inputMode="tel"
              description="Si es del exterior, escribilo con + y el código de país"
              {...(form.values.phone.startsWith('+')
                ? {}
                : { leftSection: <Text size="xl" fw={700} c="dimmed">+54 9</Text>, leftSectionWidth: 90 })}
              {...form.getInputProps('phone')}
              onChange={(e) => {
                const crudo = e.currentTarget.value;
                const internacional = crudo.trimStart().startsWith('+');
                form.setFieldValue('phone', (internacional ? '+' : '') + crudo.replace(/\D/g, ''));
              }}
            />


            <TextInput label="Email" inputMode="email" {...form.getInputProps('email')} />
            <TextInput label="Ciudad" {...form.getInputProps('city')} />

            <Box>
              <Text c={BRAND.forvetNavy} size="xl" fw={600} mb="sm">¿Hace cuánto te recibiste?</Text>
              <OpcionesGrandes
                options={YEARS_OPTIONS}
                value={form.values.yearsSinceGraduation}
                onChange={v => form.setFieldValue('yearsSinceGraduation', v)}
              />
            </Box>

            <Box>
              <Text c={BRAND.forvetNavy} size="xl" fw={600} mb={2}>¿Dónde ejercés hoy?</Text>
              <Text c="dimmed" size="md" mb="sm">Podés marcar más de una</Text>
              <OpcionesGrandes
                multiple
                options={PRACTICE_OPTIONS}
                value={form.values.practiceContext}
                onChange={v => form.setFieldValue('practiceContext', v)}
              />
            </Box>

            <Box>
              <Text c={BRAND.forvetNavy} size="xl" fw={600} mb="sm">
                ¿Ya conocías IPG Distribuidora?
              </Text>
              <OpcionesGrandes
                options={KNEW_PARTNER_OPTIONS}
                value={form.values.knewPartner}
                onChange={v => form.setFieldValue('knewPartner', v)}
              />
            </Box>

            <Box>
              <Group justify="space-between" mb="sm">
                <Text  c={BRAND.forvetNavy} size="xl" fw={600}>¿En qué te gustaría profundizar?</Text>
                <Text size="lg" fw={700} c="forvet.7">
                  ELEGÍ HASTA {MAX_INTERESTS} · {form.values.interestAreas.length}/{MAX_INTERESTS}
                </Text>
              </Group>
              <OpcionesGrandes
                multiple
                options={INTEREST_OPTIONS}
                value={form.values.interestAreas}
                onChange={toggleArea}
              />
            </Box>

            <Textarea
              label="¿Qué caso te hace dudar últimamente? (opcional)"
              autosize minRows={2}
              {...form.getInputProps('doubtCase')}
            />

            <Stack gap="md">
              <Checkbox size="lg"
                label={`Acepto que ${MARCAS} me contacten y guarden mis datos. (requerido)`}
                {...form.getInputProps('consentData', { type: 'checkbox' })} />
              <Checkbox size="lg"
                label="Autorizo el uso de mi imagen en fotos y video."
                {...form.getInputProps('consentImage', { type: 'checkbox' })} />
            </Stack>
          </Stack>
        </ScrollArea>

        <Box px="8%" py="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
          <Button type="submit" fullWidth h={72}
                  loading={loading} disabled={!form.values.consentData}>
            {flujo === 'RULETA' ? 'GIRAR' : 'GUARDAR Y CONTINUAR'}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}